import { ChevronDown } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import { resolvePreset, DateRangeFilter, type DateRange } from '@/components/shared/date-range-filter';
import { MemberNameLink, memberIdForNameColumn } from '@/components/shared/member-name-link';
import { MoneyDisplay } from '@/components/shared/money-display';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { Tip } from '@/components/shared/tip';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Listbox, type ListboxOption } from '@/components/ui/listbox';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import { useChurchStore } from '@/stores/church-store';
import { useAuthStore } from '@/stores/auth-store';
import { usePreferences } from '@/stores/preferences-store';
import { cn } from '@/utils/cn';
import { formatMoney, formatNumber } from '@/utils/format';

import { ChronicleDocumentView, ChronicleTimelineView } from './chronicle-report-views';
import { downloadReportCsv } from './download-report-csv';
import {
  downloadChronicleDocumentPdf,
  downloadChronicleTimelinePdfExport,
  downloadReportPdfExport,
} from './download-report-pdf';
import { formatReportCellValue, reportRowIsLiability } from './format-report-export';
import { ReportExportMenu } from './report-export-menu';
import { ReportFavoriteButton } from './report-favorite-button';
import {
  activeReportFilterQuery,
  ReportFiltersBar,
  type ReportFilterDefinition,
} from './report-filters-bar';
import { ReportRunSkeleton, reportRunSummaryCardCount } from './report-run-skeleton';
import { ReportsSubnav } from './reports-subnav';
import {
  buildSeededCustomReportDraft,
  canSaveSeededAsCustom,
  isSeededReportAltered,
} from './seeded-report-custom-draft';

interface ReportDefinition {
  key: string;
  category: 'finance' | 'people' | 'attendance' | 'communication' | 'governance';
  nameKey: string;
  descriptionKey: string;
  supportsDateRange: boolean;
  filters?: ReportFilterDefinition[];
}

interface ReportColumn {
  key: string;
  type: 'string' | 'number' | 'money' | 'date';
}

interface ReportRunResult {
  reportKey: string;
  columns: ReportColumn[];
  omittedColumns: string[];
  rows: Record<string, unknown>[];
  page: { page: number; pageSize: number; totalItems: number; totalPages: number };
  totals?: {
    amountMinor?: number;
    count?: number;
    currency?: string;
    byCurrency?: Array<{ currency: string; amountMinor: number; count?: number }>;
    totalMembers?: number;
    revenueMinor?: number;
    expenseMinor?: number;
    surplusMinor?: number;
    totalAssetsMinor?: number;
    totalLiabilitiesMinor?: number;
    totalNetAssetsMinor?: number;
    isBalanced?: boolean;
    differenceMinor?: number;
    asOfDate?: string;
  };
}

const CATEGORY_ORDER = ['finance', 'people', 'attendance', 'communication', 'governance'] as const;

type ReportCategory = (typeof CATEGORY_ORDER)[number];

const CATEGORY_OPEN_STORAGE_KEY = 'trinity.reports.categories.open';

const readCategoryOpenState = (): Partial<Record<ReportCategory, boolean>> => {
  try {
    const raw = localStorage.getItem(CATEGORY_OPEN_STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as Partial<Record<ReportCategory, boolean>>;
  } catch {
    return {};
  }
};

const persistCategoryOpenState = (state: Partial<Record<ReportCategory, boolean>>): void => {
  try {
    localStorage.setItem(CATEGORY_OPEN_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore quota / private-mode failures.
  }
};

class ChronicleMomentRequiredError extends Error {}

export const ReportsPage = () => {
  const { t } = useTranslation('reports');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const navigate = useNavigate();
  const membership = useCurrentMembership();
  const church = useChurchStore((state) => state.church);
  const churchId = church?.id;
  const userId = useAuthStore((state) => state.user?.id);
  const allowsFeature = useChurchStore((state) => state.allowsFeature);
  const canExport = hasPermission(membership, PERMISSIONS.REPORTS_EXPORT);
  const canCreateCustom =
    allowsFeature('custom_reports') && hasPermission(membership, PERMISSIONS.REPORTS_CREATE);
  const locale = usePreferences((state) => state.locale);
  const timezone = usePreferences((state) => state.timezone);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedKey = searchParams.get('report');
  const [dateRange, setDateRange] = useState<DateRange>(() => ({
    preset: 'thisMonth',
    ...resolvePreset('thisMonth', timezone),
  }));
  const [page, setPage] = useState(1);
  const [categoryOpen, setCategoryOpen] = useState(() => readCategoryOpenState());
  const linkedMomentId =
    selectedKey === 'chronicle_moments' ? searchParams.get('entryId') : null;
  const [reportFilters, setReportFilters] = useState<Record<string, string>>(() => {
    const filters: Record<string, string> = {};
    if (linkedMomentId) filters.entryId = linkedMomentId;
    return filters;
  });
  const [chronicleForm, setChronicleForm] = useState<'document' | 'timeline' | 'list'>(
    linkedMomentId ? 'document' : 'list',
  );
  const [filtersForReport, setFiltersForReport] = useState(selectedKey);
  if (filtersForReport !== selectedKey) {
    const entryId = selectedKey === 'chronicle_moments' ? searchParams.get('entryId') : null;
    setFiltersForReport(selectedKey);
    setChronicleForm(entryId ? 'document' : 'list');
    setReportFilters(entryId ? { entryId } : {});
    setPage(1);
  }

  const dateFrom = dateRange.from;
  const dateTo = dateRange.to;

  const reports = useQuery({
    queryKey: ['reports'],
    queryFn: () => api.get<{ data: ReportDefinition[] }>('/reports'),
  });

  const grouped = useMemo(() => {
    const map = new Map<string, ReportDefinition[]>();
    for (const report of reports.data?.data ?? []) {
      const list = map.get(report.category) ?? [];
      list.push(report);
      map.set(report.category, list);
    }
    return map;
  }, [reports.data?.data]);

  const reportPickerOptions = useMemo((): ListboxOption[] => {
    const options: ListboxOption[] = [];
    for (const category of CATEGORY_ORDER) {
      const items = grouped.get(category);
      if (!items?.length) continue;
      for (const item of items) {
        options.push({
          value: item.key,
          label: t(item.nameKey, item.key),
          group: t(`category.${category}`),
        });
      }
    }
    return options;
  }, [grouped, t]);

  const selected = reports.data?.data.find((report) => report.key === selectedKey);
  const chronicleDocument = selectedKey === 'chronicle_moments' && chronicleForm === 'document';
  const activeFilters = useMemo(() => {
    const query = activeReportFilterQuery(selected?.filters ?? [], reportFilters);
    if (selectedKey !== 'chronicle_moments') return query;
    if (chronicleForm === 'document') {
      return query.entryId ? { entryId: query.entryId } : {};
    }
    delete query.entryId;
    return query;
  }, [selected?.filters, reportFilters, selectedKey, chronicleForm]);

  const isAltered = useMemo(
    () =>
      selected
        ? isSeededReportAltered({
            supportsDateRange: selected.supportsDateRange,
            dateRange,
            timezone,
            reportFilters,
          })
        : false,
    [selected, dateRange, timezone, reportFilters],
  );

  const showSaveAsCustom =
    canCreateCustom && Boolean(selected && canSaveSeededAsCustom(selected.key) && isAltered);

  const isCategoryOpen = (category: ReportCategory) => categoryOpen[category] ?? true;

  const toggleCategory = (category: ReportCategory) => {
    setCategoryOpen((previous) => {
      const next = { ...previous, [category]: !(previous[category] ?? true) };
      persistCategoryOpenState(next);
      return next;
    });
  };

  useEffect(() => {
    if (!selectedKey || reports.isLoading) return;
    const report = reports.data?.data.find((item) => item.key === selectedKey);
    if (!report) {
      setSearchParams({}, { replace: true });
    }
  }, [reports.data?.data, reports.isLoading, selectedKey, setSearchParams]);

  useEffect(() => {
    if (!selectedKey) return;
    const report = reports.data?.data.find((item) => item.key === selectedKey);
    if (!report) return;
    setCategoryOpen((previous) => {
      if (previous[report.category as ReportCategory] ?? true) return previous;
      const next = { ...previous, [report.category]: true };
      persistCategoryOpenState(next);
      return next;
    });
  }, [selectedKey, reports.data?.data]);

  const runDateFrom = chronicleDocument ? undefined : (dateFrom ?? undefined);
  const runDateTo = chronicleDocument ? undefined : (dateTo ?? undefined);
  const runPageSize =
    selectedKey === 'chronicle_moments' && chronicleForm === 'timeline' ? 100 : 25;

  const run = useQuery({
    queryKey: [
      'reports',
      'run',
      selectedKey,
      runDateFrom,
      runDateTo,
      page,
      runPageSize,
      activeFilters,
    ],
    enabled: Boolean(selectedKey) && (!chronicleDocument || Boolean(activeFilters.entryId)),
    refetchOnWindowFocus: false,
    queryFn: () =>
      api.get<ReportRunResult>(`/reports/${selectedKey}/run`, {
        query: {
          page,
          pageSize: runPageSize,
          dateFrom: runDateFrom,
          dateTo: runDateTo,
          ...activeFilters,
        },
      }),
  });

  const exportReport = useMutation({
    mutationFn: async (format: 'csv' | 'pdf') => {
      if (!selectedKey || !churchId || !selected) return;
      if (chronicleDocument && !activeFilters.entryId) {
        throw new ChronicleMomentRequiredError();
      }
      if (format === 'csv') {
        await downloadReportCsv({
          churchId,
          reportKey: selectedKey,
          dateFrom: runDateFrom,
          dateTo: runDateTo,
          filters: activeFilters,
        });
        return;
      }
      const pdfInput = {
        reportKey: selectedKey,
        reportTitle: reportName(selected),
        reportDescription: reportDescription(selected),
        churchName: church?.name ?? t('title'),
        branchName: church?.campusName,
        logoUrl: church?.logoUrl,
        baseCurrency: church?.baseCurrency ?? 'GHS',
        dateFrom: runDateFrom,
        dateTo: runDateTo,
        filters: activeFilters,
        supportsDateRange: selected.supportsDateRange && !chronicleDocument,
        locale,
        timezone,
        t,
        tCommon,
      };
      if (selectedKey === 'chronicle_moments' && chronicleForm === 'timeline') {
        await downloadChronicleTimelinePdfExport(pdfInput);
        return;
      }
      if (chronicleDocument) {
        await downloadChronicleDocumentPdf(pdfInput);
        return;
      }
      await downloadReportPdfExport({
        ...pdfInput,
        reportDescription:
          selectedKey === 'chronicle_moments' ? t('chronicle.list') : pdfInput.reportDescription,
      });
    },
    onSuccess: (_data, format) => {
      toast.success(format === 'csv' ? t('run.exportDownloaded') : t('run.exportPdfDownloaded'));
    },
    onError: (error) => {
      if (error instanceof ChronicleMomentRequiredError) {
        toast.error(t('chronicle.momentRequired'));
        return;
      }
      if (error instanceof ApiError) toast.error(tErrors(error.code, error.params));
      else toast.error(tCommon('toast.somethingWentWrong'));
    },
  });

  const reportName = (definition: ReportDefinition) => t(definition.nameKey, definition.key);
  const reportDescription = (definition: ReportDefinition) =>
    t(definition.descriptionKey, definition.key);

  const formatCell = (column: ReportColumn, value: unknown, row: Record<string, unknown>) => {
    const memberId = memberIdForNameColumn(column.key, row);
    if (memberId && typeof value === 'string' && value.trim()) {
      return <MemberNameLink memberId={memberId} name={value} />;
    }
    if (value === null || value === undefined || value === '') return '—';
    if (column.type === 'money') {
      const currency = typeof row.currency === 'string' ? row.currency : 'GHS';
      return (
        <MoneyDisplay
          amount={Number(value)}
          currency={currency}
          colorNegative
          className={reportRowIsLiability(row) ? 'text-danger-emphasis' : undefined}
        />
      );
    }
    if (column.type === 'number') return formatNumber(Number(value));
    return formatReportCellValue(column, value, row, t, { locale });
  };

  const columnLabel = (key: string) => t(`column.${key}`, key);

  const handleSaveAsCustom = () => {
    if (!selected) return;
    const draft = buildSeededCustomReportDraft({
      reportKey: selected.key,
      reportName: reportName(selected),
      supportsDateRange: selected.supportsDateRange,
      dateFrom,
      dateTo,
      activeFilters,
    });
    if (!draft) return;
    navigate('/reports/custom/new', { state: { seededDraft: draft } });
  };

  const visibleColumns = useMemo(() => {
    if (!run.data?.rows.length) return run.data?.columns ?? [];
    const currencyColumn = run.data.columns.find((column) => column.key === 'currency');
    if (!currencyColumn) return run.data.columns;
    const currencies = new Set(
      run.data.rows
        .map((row) => row.currency)
        .filter((value): value is string => typeof value === 'string' && value.length > 0),
    );
    if (currencies.size <= 1) {
      return run.data.columns.filter((column) => column.key !== 'currency');
    }
    return run.data.columns;
  }, [run.data?.columns, run.data?.rows]);

  return (
    <div className="flex flex-col gap-6 lg:h-[calc(100dvh-3.5rem-3rem)]">
      <PageHeader title={t('title')} description={t('description')} />
      <ReportsSubnav />

      <div className="lg:hidden">
        <Listbox
          value={selectedKey}
          onChange={(value) => {
            if (!value) return;
            setSearchParams({ report: value });
            setPage(1);
          }}
          options={reportPickerOptions}
          placeholder={t('run.selectReport')}
          searchable
          searchPlaceholder={tCommon('search.search')}
          emptyMessage={tCommon('search.noResults')}
          disabled={reports.isLoading || reportPickerOptions.length === 0}
        />
      </div>

      <div className="grid min-h-0 flex-1 gap-6 lg:grid-cols-[minmax(0,280px)_1fr] lg:overflow-hidden">
        <aside className="hidden min-h-0 space-y-4 lg:block lg:overflow-y-auto lg:overscroll-contain lg:pr-1">
          {reports.isLoading ? (
            <Card>
              <CardContent className="py-6 text-body-sm text-fg-muted">
                {tCommon('state.loading')}
              </CardContent>
            </Card>
          ) : (reports.data?.data.length ?? 0) === 0 ? (
            <Card>
              <CardContent className="py-6">
                <p className="text-card-title text-fg">{t('empty.title')}</p>
                <p className="mt-1 text-body-sm text-fg-muted">{t('empty.body')}</p>
              </CardContent>
            </Card>
          ) : (
            CATEGORY_ORDER.filter((category) => grouped.has(category)).map((category) => {
              const open = isCategoryOpen(category);
              const categoryLabel = t(`category.${category}`);

              return (
                <div key={category}>
                  <button
                    type="button"
                    className="mb-1 flex w-full items-center gap-1.5 rounded-md px-1 py-1.5 text-left text-caption font-medium uppercase tracking-wide text-fg-tertiary hover:bg-surface-sunken hover:text-fg-secondary"
                    aria-expanded={open}
                    aria-label={
                      open
                        ? t('category.collapseSection', { category: categoryLabel })
                        : t('category.expandSection', { category: categoryLabel })
                    }
                    onClick={() => toggleCategory(category)}
                  >
                    <ChevronDown
                      aria-hidden
                      className={cn(
                        'size-3.5 shrink-0 transition-transform',
                        open ? 'rotate-0' : '-rotate-90',
                      )}
                    />
                    {categoryLabel}
                  </button>
                  {open ? (
                    <ul className="space-y-1 pb-3">
                      {(grouped.get(category) ?? []).map((item) => (
                        <li key={item.key} className="flex items-center gap-0.5">
                          <button
                            type="button"
                            onClick={() => {
                              setSearchParams({ report: item.key });
                              setPage(1);
                            }}
                            className={`min-w-0 flex-1 rounded-md px-3 py-2 text-left text-body-sm transition-colors ${
                              selectedKey === item.key
                                ? 'bg-brand/10 font-medium text-brand'
                                : 'text-fg-secondary hover:bg-surface-sunken hover:text-fg'
                            }`}
                          >
                            {reportName(item)}
                          </button>
                          <ReportFavoriteButton
                            favoriteRef={{ kind: 'seeded', key: item.key }}
                            churchId={churchId}
                            userId={userId}
                          />
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              );
            })
          )}
        </aside>

        <section className="min-h-0 lg:overflow-y-auto lg:overscroll-contain">
          {!selected ? (
            <Card>
              <CardContent className="py-8 text-center text-body-sm text-fg-muted">
                {t('run.selectReport')}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              <Card>
                <CardContent className="space-y-4 py-6">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-0.5">
                        <h2 className="text-card-title leading-none text-fg">{reportName(selected)}</h2>
                        <ReportFavoriteButton
                          favoriteRef={{ kind: 'seeded', key: selected.key }}
                          churchId={churchId}
                          userId={userId}
                        />
                      </div>
                      <p className="mt-1 text-body-sm text-fg-muted">{reportDescription(selected)}</p>
                    </div>

                    <div className="flex shrink-0 justify-end gap-2">
                      {showSaveAsCustom ? (
                        <Button variant="secondary" onClick={handleSaveAsCustom}>
                          {t('run.saveAsCustom')}
                        </Button>
                      ) : null}
                      {canExport ? (
                        <ReportExportMenu
                          loading={exportReport.isPending}
                          format={exportReport.variables}
                          onExport={(format) => exportReport.mutate(format)}
                        />
                      ) : null}
                      <Button onClick={() => void run.refetch()} loading={run.isFetching}>
                        {t('run.run')}
                      </Button>
                    </div>
                  </div>

                  {selected.supportsDateRange && !chronicleDocument ? (
                    <Tip tipId="reports.dateRange" />
                  ) : null}

                  {selected.key === 'chronicle_moments' ||
                  selected.supportsDateRange ||
                  (selected.filters?.length ?? 0) > 0 ? (
                    <div className="flex flex-wrap items-end gap-2">
                      {selected.key === 'chronicle_moments' ? (
                        <div className="w-44 min-w-[10rem]">
                          <Listbox
                            value={chronicleForm}
                            onChange={(next) => {
                              if (next !== 'document' && next !== 'timeline' && next !== 'list') return;
                              setChronicleForm(next);
                              setPage(1);
                              if (next !== 'document') {
                                setReportFilters((previous) => {
                                  const { entryId: _entryId, ...rest } = previous;
                                  return rest;
                                });
                              }
                            }}
                            options={[
                              { value: 'document', label: t('chronicle.document') },
                              { value: 'timeline', label: t('chronicle.timeline') },
                              { value: 'list', label: t('chronicle.list') },
                            ]}
                            aria-label={t('chronicle.form')}
                          />
                        </div>
                      ) : null}
                      {selected.supportsDateRange && !chronicleDocument ? (
                        <DateRangeFilter
                          value={dateRange}
                          onChange={(range) => {
                            setDateRange(range);
                            setPage(1);
                          }}
                          labels={{
                            today: tCommon('date.today'),
                            yesterday: tCommon('date.yesterday'),
                            thisWeek: tCommon('date.thisWeek'),
                            lastWeek: tCommon('date.lastWeek'),
                            thisMonth: tCommon('date.thisMonth'),
                            lastMonth: tCommon('date.lastMonth'),
                            thisQuarter: tCommon('date.thisQuarter'),
                            thisYear: tCommon('date.thisYear'),
                            lastYear: tCommon('date.lastYear'),
                            allTime: tCommon('date.allTime'),
                            custom: tCommon('date.custom'),
                            from: t('run.dateFrom'),
                            to: t('run.dateTo'),
                          }}
                        />
                      ) : null}
                      <ReportFiltersBar
                        filters={(selected.filters ?? []).filter((filter) =>
                          selected.key === 'chronicle_moments'
                            ? chronicleForm === 'document'
                              ? filter.kind === 'chronicleEntry'
                              : filter.kind !== 'chronicleEntry'
                            : true,
                        )}
                        values={reportFilters}
                        onChange={(key, value) => {
                          setReportFilters((previous) => ({ ...previous, [key]: value }));
                          setPage(1);
                        }}
                      />
                    </div>
                  ) : null}
                </CardContent>
              </Card>

              {run.data?.omittedColumns.length && !run.isFetching ? (
                <p className="text-body-sm text-fg-muted">{t('run.omittedColumns')}</p>
              ) : null}

              {chronicleDocument && !activeFilters.entryId ? (
                <Card>
                  <CardContent className="py-8 text-center text-body-sm text-fg-muted">
                    {t('chronicle.documentEmpty')}
                  </CardContent>
                </Card>
              ) : run.isFetching ? (
                <ReportRunSkeleton
                  summaryCards={reportRunSummaryCardCount(selected.key, run.data?.totals)}
                  columnCount={visibleColumns.length || 3}
                />
              ) : run.isError ? (
                <Card>
                  <CardContent className="py-6 text-body-sm text-danger-fg">
                    {tCommon('state.errorBody')}
                  </CardContent>
                </Card>
              ) : run.data ? (
                <>
                  {run.data.totals?.revenueMinor !== undefined ? (
                    <div className="grid gap-3 sm:grid-cols-3">
                      {[
                        { label: t('totals.revenue'), amount: run.data.totals.revenueMinor },
                        { label: t('totals.expenses'), amount: run.data.totals.expenseMinor },
                        { label: t('totals.surplus'), amount: run.data.totals.surplusMinor },
                      ].map((item) => (
                        <Card key={item.label}>
                          <CardContent className="py-4">
                            <p className="text-caption text-fg-secondary">{item.label}</p>
                            <MoneyDisplay
                              amount={Number(item.amount ?? 0)}
                              currency={run.data!.totals?.currency ?? 'GHS'}
                              size="lg"
                              colorNegative
                            />
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : null}

                  {run.data.totals?.totalAssetsMinor !== undefined ? (
                    <div className="space-y-3">
                      <div className="grid gap-3 sm:grid-cols-3">
                        {[
                          { label: t('totals.totalAssets'), amount: run.data.totals.totalAssetsMinor },
                          {
                            label: t('totals.totalLiabilities'),
                            amount: run.data.totals.totalLiabilitiesMinor,
                            liability: true,
                          },
                          {
                            label: t('totals.totalNetAssets'),
                            amount: run.data.totals.totalNetAssetsMinor,
                          },
                        ].map((item) => (
                          <Card key={item.label}>
                            <CardContent className="py-4">
                              <p className="text-caption text-fg-secondary">{item.label}</p>
                              <MoneyDisplay
                                amount={Number(item.amount ?? 0)}
                                currency={run.data!.totals?.currency ?? 'GHS'}
                                size="lg"
                                colorNegative
                                className={item.liability ? 'text-danger-emphasis' : undefined}
                              />
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                      <p className="text-body-sm text-fg-muted">
                        {run.data.totals.isBalanced
                          ? t('totals.balanced')
                          : t('totals.unbalanced', {
                              amount: formatMoney(
                                Math.abs(Number(run.data.totals.differenceMinor ?? 0)),
                                run.data.totals.currency ?? 'GHS',
                              ),
                            })}
                      </p>
                    </div>
                  ) : null}

                  {run.data.totals?.amountMinor !== undefined &&
                  run.data.totals.revenueMinor === undefined &&
                  !Array.isArray(run.data.totals.byCurrency) ? (
                    <Card>
                      <CardContent className="flex items-center justify-between py-4">
                        <span className="text-body-sm text-fg-secondary">{tCommon('table.total')}</span>
                        <MoneyDisplay
                          amount={run.data.totals.amountMinor}
                          currency={run.data.totals.currency ?? 'GHS'}
                          size="lg"
                          colorNegative
                        />
                      </CardContent>
                    </Card>
                  ) : null}

                  {Array.isArray(run.data.totals?.byCurrency) ? (
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {(run.data.totals.byCurrency as Array<{
                        currency: string;
                        amountMinor: number;
                      }>).map((item) => (
                        <Card key={item.currency}>
                          <CardContent className="flex items-center justify-between py-4">
                            <span className="text-body-sm text-fg-secondary">
                              {tCommon('table.total')} ({item.currency})
                            </span>
                            <MoneyDisplay
                              amount={item.amountMinor}
                              currency={item.currency}
                              size="lg"
                              colorNegative
                            />
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : null}

                  <Card>
                    <CardContent className="overflow-x-auto p-0">
                      {selected.key === 'chronicle_moments' && chronicleForm === 'document' ? (
                        run.data.rows[0] ? (
                          <ChronicleDocumentView row={run.data.rows[0]} locale={locale} />
                        ) : (
                          <p className="px-4 py-8 text-center text-fg-muted">
                            {tCommon('table.noResults')}
                          </p>
                        )
                      ) : selected.key === 'chronicle_moments' && chronicleForm === 'timeline' ? (
                        run.data.rows.length === 0 ? (
                          <p className="px-4 py-8 text-center text-fg-muted">
                            {tCommon('table.noResults')}
                          </p>
                        ) : (
                          <ChronicleTimelineView rows={run.data.rows} locale={locale} />
                        )
                      ) : (
                      <table className="w-full table-fixed text-body-sm">
                        <thead>
                          <tr className="border-b border-border text-left text-caption text-fg-secondary">
                            {visibleColumns.map((column) => (
                              <th
                                key={column.key}
                                className={`px-4 py-3 font-medium ${
                                  column.type === 'money' || column.type === 'number'
                                    ? 'text-right'
                                    : ''
                                } ${
                                  column.key === 'fundName' ||
                                  column.key === 'lineLabel' ||
                                  column.key === 'programName'
                                    ? 'w-[40%]'
                                    : column.key === 'currency'
                                      ? 'w-24'
                                      : column.type === 'money'
                                        ? 'w-36'
                                        : column.type === 'number'
                                          ? 'w-24'
                                          : ''
                                }`}
                              >
                                {columnLabel(column.key)}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {run.data.rows.length === 0 ? (
                            <tr>
                              <td
                                colSpan={visibleColumns.length}
                                className="px-4 py-8 text-center text-fg-muted"
                              >
                                {tCommon('table.noResults')}
                              </td>
                            </tr>
                          ) : (
                            run.data.rows.map((row, index) => (
                              <tr key={index} className="border-b border-border last:border-0">
                                {visibleColumns.map((column) => (
                                  <td
                                    key={column.key}
                                    className={cn(
                                      'px-4 py-3 align-top',
                                      column.key === 'body' && 'whitespace-pre-wrap',
                                      (column.type === 'money' || column.type === 'number') &&
                                        'text-right tabular-nums whitespace-nowrap',
                                    )}
                                  >
                                    {formatCell(column, row[column.key], row)}
                                  </td>
                                ))}
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                      )}
                    </CardContent>
                  </Card>

                  <Pagination
                    page={run.data.page.page}
                    pageSize={run.data.page.pageSize}
                    totalItems={run.data.page.totalItems}
                    totalPages={run.data.page.totalPages}
                    onPageChange={setPage}
                    labels={{
                      showing: ({ from, to, total }) =>
                        tCommon('pagination.showing', { from, to, total }),
                      perPage: tCommon('pagination.perPage'),
                      first: tCommon('pagination.first'),
                      previous: tCommon('pagination.previous'),
                      next: tCommon('pagination.next'),
                      last: tCommon('pagination.last'),
                    }}
                  />
                </>
              ) : null}
            </div>
          )}

        </section>
      </div>
    </div>
  );
};
